import { useMemo, useRef, useState } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Tabs,
  Tab,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Badge,
  Button,
  Banner,
  EmptyState,
  CircularLoader,
  Search,
  SegmentedControl,
  SegmentedControlButton,
  ContextualMenu,
  ContextualMenuItem,
  Divider,
  PlusIcon,
  LockIcon,
  GroupIcon,
  MoreVerticalIcon,
  EditIcon,
  TrashIcon,
  CheckIcon,
  useDocumentTitle,
} from '@forge/common';
import { useRole } from '@/context/RoleContext';
import { useApiMutation } from '@/hooks/useApiMutation';
import { useSftpUsers } from '@/hooks/useSftpUsers';
import { useSftpIpBlocks } from '@/hooks/useSftpIpBlocks';
import { sftpUsersApi } from '@/api/sftpUsers';
import { sftpIpBlocksApi } from '@/api/sftpIpBlocks';
import type { SftpUser } from '@/api/sftpUsers';
import type { SftpIpBlock } from '@/api/sftpIpBlocks';
import MockDataBanner from '@/pages/access-management/components/MockDataBanner';
import RotateKeyDialog from './components/RotateKeyDialog';
import EditSftpUserDialog from './components/EditSftpUserDialog';
import BlockIpDialog from './components/BlockIpDialog';

type ActiveTab = 'users' | 'blocked-ips';
type StatusFilter = 'all' | 'active' | 'inactive';

const formatDate = (value: string | null): string => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

interface SftpUserRowProps {
  user: SftpUser;
  onRotateKey: (user: SftpUser) => void;
  onEdit: (user: SftpUser) => void;
  onDeactivate: (user: SftpUser) => void;
  onReactivate: (user: SftpUser) => void;
}

const SftpUserRow = ({
  user,
  onRotateKey,
  onEdit,
  onDeactivate,
  onReactivate,
}: SftpUserRowProps) => {
  const actionsRef = useRef<HTMLButtonElement>(null);

  return (
    <TableRow>
      <TableCell>
        <span className="font-mono text-xs">{user.username}</span>
      </TableCell>
      <TableCell>{user.clientName || '—'}</TableCell>
      <TableCell>
        {user.homeDirectory ? (
          <span className="font-mono text-xs">{user.homeDirectory}</span>
        ) : (
          <span className="text-xs text-color-tertiary">Default</span>
        )}
      </TableCell>
      <TableCell>
        {user.allowedSourceCidr ? (
          <span className="font-mono text-xs">{user.allowedSourceCidr}</span>
        ) : (
          <span className="text-xs text-color-tertiary">Any</span>
        )}
      </TableCell>
      <TableCell>
        <Badge color={user.isActive ? 'green' : 'gray'}>
          {user.isActive ? 'Active' : 'Inactive'}
        </Badge>
      </TableCell>
      <TableCell>
        <span className="text-xs text-color-tertiary">{formatDate(user.createdAt)}</span>
      </TableCell>
      <TableCell>
        <div className="flex justify-end">
          <Button
            ref={actionsRef}
            variant="tertiary"
            aria-label={`Actions for ${user.username}`}
            icon={<MoreVerticalIcon />}
          />
          <ContextualMenu anchor={actionsRef} position="bottom-right">
            <ContextualMenuItem
              as="button"
              id="rotate-key"
              label="Rotate key"
              prefix={<LockIcon />}
              onClick={() => onRotateKey(user)}
            />
            <ContextualMenuItem
              as="button"
              id="edit-user"
              label="Edit CIDR / home"
              prefix={<EditIcon />}
              onClick={() => onEdit(user)}
            />
            <Divider />
            {user.isActive ? (
              <ContextualMenuItem
                as="button"
                id="deactivate-user"
                label="Deactivate"
                prefix={<TrashIcon />}
                onClick={() => onDeactivate(user)}
              />
            ) : (
              <ContextualMenuItem
                as="button"
                id="reactivate-user"
                label="Reactivate"
                prefix={<CheckIcon />}
                onClick={() => onReactivate(user)}
              />
            )}
          </ContextualMenu>
        </div>
      </TableCell>
    </TableRow>
  );
};

interface IpBlockRowProps {
  block: SftpIpBlock;
  onUnblock: (block: SftpIpBlock) => void;
}

const IpBlockRow = ({ block, onUnblock }: IpBlockRowProps) => (
  <TableRow>
    <TableCell>
      <span className="font-mono text-xs">{block.ipAddress}</span>
    </TableCell>
    <TableCell>{block.reason || '—'}</TableCell>
    <TableCell>
      <span className="tabular-nums">{block.failCount}</span>
    </TableCell>
    <TableCell>
      <span className="text-xs text-color-tertiary">{formatDate(block.blockedAt)}</span>
    </TableCell>
    <TableCell>
      <span className="text-xs text-color-tertiary">{formatDate(block.expiresAt)}</span>
    </TableCell>
    <TableCell>
      <Badge color={block.isActive ? 'orange' : 'gray'}>
        {block.isActive ? 'Blocked' : 'Expired'}
      </Badge>
    </TableCell>
    <TableCell>
      <div className="flex justify-end">
        <Button
          variant="tertiary"
          size="regular"
          icon={<CheckIcon />}
          disabled={!block.isActive}
          onClick={() => onUnblock(block)}
        >
          Unblock
        </Button>
      </div>
    </TableCell>
  </TableRow>
);

const SftpAccessPage = () => {
  useDocumentTitle('SFTP Access — OneRing Portal');
  const { isAdmin } = useRole();

  const [activeTab, setActiveTab] = useState<ActiveTab>('users');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  const [rotateKeyUser, setRotateKeyUser] = useState<SftpUser | undefined>();
  const [editUser, setEditUser] = useState<SftpUser | undefined>();
  const [showBlockIp, setShowBlockIp] = useState(false);

  const isActiveParam =
    statusFilter === 'all' ? undefined : statusFilter === 'active';

  const {
    sftpUsers,
    isLoading: usersLoading,
    error: usersError,
    isUsingMockData: usersMock,
    reload: reloadUsers,
  } = useSftpUsers({
    q: search.trim() || undefined,
    isActive: isActiveParam,
    enabled: isAdmin,
  });

  const {
    ipBlocks,
    isLoading: blocksLoading,
    error: blocksError,
    isUsingMockData: blocksMock,
    reload: reloadBlocks,
  } = useSftpIpBlocks({ enabled: isAdmin && activeTab === 'blocked-ips' });

  const { mutate: deactivateUser } = useApiMutation({
    mutationFn: (id: string) => sftpUsersApi.deactivate(id),
    successMessage: 'SFTP credential deactivated',
    errorMessage: 'Failed to deactivate SFTP credential',
    onSuccess: () => reloadUsers(),
  });

  const { mutate: reactivateUser } = useApiMutation({
    mutationFn: (id: string) => sftpUsersApi.update(id, { isActive: true }),
    successMessage: 'SFTP credential reactivated',
    errorMessage: 'Failed to reactivate SFTP credential',
    onSuccess: () => reloadUsers(),
  });

  const { mutate: unblockIp } = useApiMutation({
    mutationFn: (id: string) => sftpIpBlocksApi.remove(id),
    successMessage: 'IP address unblocked',
    errorMessage: 'Failed to unblock IP address',
    onSuccess: () => reloadBlocks(),
  });

  const isUsingMockData = useMemo(
    () => (activeTab === 'users' ? usersMock : blocksMock),
    [activeTab, usersMock, blocksMock],
  );

  if (!isAdmin) {
    return (
      <div className="p-4">
        <h2 className="text-xl font-bold mb-4">SFTP Access</h2>
        <Banner
          variant="inline"
          type="warning"
          title="Access restricted"
          description="You need the platform administrator role to manage SFTP access."
          canClose={false}
        />
      </div>
    );
  }

  return (
    <div className="p-4">
      <h2 className="text-xl font-bold mb-1">SFTP Access</h2>
      <p className="text-sm text-color-tertiary mb-4">
        Manage SFTP credentials and gateway IP blocks across all partners and clients.
      </p>

      {isUsingMockData && (
        <div className="mb-4">
          <MockDataBanner featureName="SFTP Access" />
        </div>
      )}

      <Tabs value={activeTab} onChange={(id) => setActiveTab(id as ActiveTab)}>
        <Tab id="users" active={activeTab === 'users'}>
          {`SFTP Users (${sftpUsers.length})`}
        </Tab>
        <Tab id="blocked-ips" active={activeTab === 'blocked-ips'}>
          {`Blocked IPs (${ipBlocks.length})`}
        </Tab>
      </Tabs>

      {activeTab === 'users' && (
        <div className="mt-4">
          {usersError ? (
            <Banner
              variant="inline"
              type="error"
              title="Failed to load SFTP users"
              description={usersError}
              canClose={false}
            />
          ) : (
            <Card>
              <CardHeader
                title="SFTP Users"
                subtitle={`${sftpUsers.length} credential${sftpUsers.length !== 1 ? 's' : ''}`}
                slotRight={
                  <div className="flex items-center gap-3">
                    <Search
                      id="sftp-user-search"
                      placeholder="Search users..."
                      onChange={(e) => setSearch(e.target.value)}
                    />
                    <SegmentedControl
                      id="sftp-user-status"
                      size="small"
                      onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
                    >
                      <SegmentedControlButton
                        id="status-all"
                        value="all"
                        defaultChecked={statusFilter === 'all'}
                      >
                        All
                      </SegmentedControlButton>
                      <SegmentedControlButton
                        id="status-active"
                        value="active"
                        defaultChecked={statusFilter === 'active'}
                      >
                        Active
                      </SegmentedControlButton>
                      <SegmentedControlButton
                        id="status-inactive"
                        value="inactive"
                        defaultChecked={statusFilter === 'inactive'}
                      >
                        Inactive
                      </SegmentedControlButton>
                    </SegmentedControl>
                  </div>
                }
              />
              <CardContent>
                {usersLoading ? (
                  <div className="flex min-h-[240px] items-center justify-center">
                    <CircularLoader size="large" />
                  </div>
                ) : sftpUsers.length === 0 ? (
                  <EmptyState icon={<GroupIcon />} description="No SFTP users found" />
                ) : (
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Username</TableCell>
                        <TableCell>Owner</TableCell>
                        <TableCell>Home Directory</TableCell>
                        <TableCell>Allowed Source CIDR</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Created</TableCell>
                        <TableCell />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sftpUsers.map((user) => (
                        <SftpUserRow
                          key={user.id}
                          user={user}
                          onRotateKey={setRotateKeyUser}
                          onEdit={setEditUser}
                          onDeactivate={(u) => deactivateUser(u.id)}
                          onReactivate={(u) => reactivateUser(u.id)}
                        />
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {activeTab === 'blocked-ips' && (
        <div className="mt-4">
          {blocksError ? (
            <Banner
              variant="inline"
              type="error"
              title="Failed to load blocked IPs"
              description={blocksError}
              canClose={false}
            />
          ) : (
            <Card>
              <CardHeader
                title="Blocked IPs"
                subtitle={`${ipBlocks.length} block${ipBlocks.length !== 1 ? 's' : ''}`}
                slotRight={
                  <Button
                    variant="primary"
                    icon={<PlusIcon />}
                    onClick={() => setShowBlockIp(true)}
                  >
                    Block IP
                  </Button>
                }
              />
              <CardContent>
                {blocksLoading ? (
                  <div className="flex min-h-[240px] items-center justify-center">
                    <CircularLoader size="large" />
                  </div>
                ) : ipBlocks.length === 0 ? (
                  <EmptyState icon={<LockIcon />} description="No IP blocks" />
                ) : (
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>IP Address</TableCell>
                        <TableCell>Reason</TableCell>
                        <TableCell>Failures</TableCell>
                        <TableCell>Blocked</TableCell>
                        <TableCell>Expires</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {ipBlocks.map((block) => (
                        <IpBlockRow
                          key={block.id}
                          block={block}
                          onUnblock={(b) => unblockIp(b.id)}
                        />
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {rotateKeyUser && (
        <RotateKeyDialog
          sftpUser={rotateKeyUser}
          onClose={() => setRotateKeyUser(undefined)}
          onSuccess={() => {
            setRotateKeyUser(undefined);
            reloadUsers();
          }}
        />
      )}

      {editUser && (
        <EditSftpUserDialog
          sftpUser={editUser}
          onClose={() => setEditUser(undefined)}
          onSuccess={() => {
            setEditUser(undefined);
            reloadUsers();
          }}
        />
      )}

      {showBlockIp && (
        <BlockIpDialog
          onClose={() => setShowBlockIp(false)}
          onSuccess={() => {
            setShowBlockIp(false);
            reloadBlocks();
          }}
        />
      )}
    </div>
  );
};

export default SftpAccessPage;
